import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Container } from '../styles/styled';
import emailjs from '@emailjs/browser';

const JoinHurrlinkWrapper = styled.section`
    padding: 60px 0;
    text-align: center;

    @media (max-width: 768px) {
        padding: 40px 0;
    }

    @media (max-width: 480px) {
        padding: 30px 0;
    }

    > div {
        > h2 {
            font-size: 36px;
            color: #002DFF;
            margin-bottom: 20px;

            @media (max-width: 768px) {
                font-size: 28px;
            }

            @media (max-width: 480px) {
                font-size: 24px;
            }
        }

        > p {
            font-size: 18px;
            color: #555;
            line-height: 1.6;
            margin-bottom: 40px;

            @media (max-width: 768px) {
                font-size: 16px;
                margin-bottom: 30px;
            }

            @media (max-width: 480px) {
                font-size: 14px;
                margin-bottom: 25px;
            }
        }
    }
`;

const FormContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 40px;

    @media (max-width: 768px) {
        margin-top: 30px;
    }
`;

const FormWrapper = styled.form`
    max-width: 600px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 30px;
    border: 1px solid #E0E0E0;
    border-radius: 10px;
    background-color: #FAFAFA;

    @media (max-width: 768px) {
        padding: 25px;
        gap: 18px;
    }

    @media (max-width: 480px) {
        padding: 20px;
        gap: 15px;
    }
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
        font-size: 16px;
        color: #002DFF;
        font-weight: 600;
        
        @media (max-width: 480px) {
            font-size: 15px;
        }
    }
    
    input, textarea {
        padding: 12px 16px;
        font-size: 16px;
        border: 1px solid #D0D0D0;
        border-radius: 8px;
        font-family: inherit;
        transition: all 0.3s ease;
        
        @media (max-width: 480px) {
            padding: 10px 14px;
            font-size: 15px;
        }
        
        &:focus {
            outline: none;
            border-color: #002DFF;
            background-color: #FFF;
        }
        
        &::placeholder {
            color: #999;
        }
    }
    
    textarea {
        min-height: 120px;
        resize: vertical;
        
        @media (max-width: 480px) {
            min-height: 100px;
        }
    }
`;

const SubmitButton = styled.button`
    padding: 14px;
    text-align: center;
    width: 100%;
    color: #FFF;
    font-weight: 700;
    border: none;
    border-radius: 10px;
    font-size: 18px;
    background-color: #002DFF;
    cursor: pointer;
    transition: all 0.3s ease;
    
    @media (max-width: 480px) {
        padding: 12px;
        font-size: 16px;
    }
    
    &:hover {
        background-color: #29f0b4ff;
        color: #002DFF;
    }
    
    &:disabled {
        background-color: #CCC;
        cursor: not-allowed;
    }
`;

const DecorativeSpan = styled.span`
    background-color: #78FFD6;
    height: fit-content;
    border-radius: 8px;
    display: block;
    width: 140px;
    color: #78FFD6;
    font-size: 37px;
    margin-bottom: -45px;
    margin-left: -10px;
    
    @media (max-width: 768px) {
        width: 110px;
        font-size: 30px;
        margin-bottom: -35px;
    }
    
    @media (max-width: 480px) {
        width: 90px;
        font-size: 24px;
        margin-bottom: -28px;
        margin-left: -5px;
    }
`;

const JoinHurrlink = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Создаем email в формате текста
        const emailBody = `
New Contact Form Submission from Hurrlink Website

Name: ${formData.name}
Email: ${formData.email}

Message:
${formData.message}

---
Sent from Hurrlink Contact Form
        `;

        // Отправка на bakhtovar.yusufi@hurrlink.com
        try {
            // Используем простой fetch для отправки через FormSubmit или другой сервис
            const response = await fetch('https://formsubmit.co/ajax/bakhtovar.yusufi@hurrlink.com', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                    _subject: `New Contact from ${formData.name} - Hurrlink`,
                    _template: 'table'
                })
            });

            if (response.ok) {
                alert(t('joinHurrlink.form.success'));
                setFormData({ name: '', email: '', message: '' });
            } else {
                alert('Error sending message. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error sending message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <JoinHurrlinkWrapper id="contact">
            <Container>
                <h2>{t('joinHurrlink.title')}</h2>
                <p>{t('joinHurrlink.description')}</p>

                <FormContainer>
                    <FormWrapper onSubmit={handleSubmit}>
                        <InputGroup>
                            <label htmlFor="name">{t('joinHurrlink.form.name.label')}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={t('joinHurrlink.form.name.placeholder')}
                                required
                            />
                        </InputGroup>

                        <InputGroup>
                            <label htmlFor="email">{t('joinHurrlink.form.email.label')}</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder={t('joinHurrlink.form.email.placeholder')}
                                required
                            />
                        </InputGroup>

                        <InputGroup>
                            <label htmlFor="message">{t('joinHurrlink.form.message.label')}</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder={t('joinHurrlink.form.message.placeholder')}
                                required
                            />
                        </InputGroup>

                        <SubmitButton type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : t('joinHurrlink.form.submit')}
                        </SubmitButton>
                    </FormWrapper>
                </FormContainer>
            </Container>
        </JoinHurrlinkWrapper>
    );
}

export default JoinHurrlink;